import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Fingerprint, Plus, Trash2, Pencil, ShieldCheck, ArrowLeft, Loader2,
    CheckCircle2, Info,
} from "lucide-react";
import {
    isPlatformAuthenticatorAvailable, isWebAuthnSupported,
    registerPasskeyForCurrentUser, reauthenticateWithPasskey,
    friendlyDeviceLabel, passkeyButtonLabel,
} from "@/lib/webauthn";

/**
 * Settings → Security → Passkeys.
 * List / Add / Rename / Remove. Removal requires recent authentication;
 * we transparently prompt the user for a passkey reauth if the server
 * returns 428.
 */
export default function PasskeysManager() {
    const [creds, setCreds]       = useState(null);
    const [supported, setSupported] = useState(false);
    const [platformAvailable, setPlatformAvailable] = useState(false);
    const [busy, setBusy]         = useState(false);
    const [addLabel, setAddLabel] = useState("");
    const [renameFor, setRenameFor] = useState(null);
    const [renameLabel, setRenameLabel] = useState("");

    const load = async () => {
        try {
            const r = await api.get("/webauthn/credentials");
            setCreds(r.data.credentials || []);
        } catch { setCreds([]); }
    };
    useEffect(() => {
        setSupported(isWebAuthnSupported());
        isPlatformAuthenticatorAvailable().then(setPlatformAvailable);
        load();
    }, []);

    const addPasskey = async () => {
        setBusy(true);
        try {
            const label = addLabel.trim() || friendlyDeviceLabel();
            await registerPasskeyForCurrentUser({ deviceLabel: label });
            toast.success("Passkey added successfully.");
            setAddLabel("");
            await load();
        } catch (e) {
            const msg = e?.message || "Failed to add passkey.";
            if (!/cancel/i.test(msg)) toast.error(msg);
        } finally { setBusy(false); }
    };

    const removePasskey = async (cred) => {
        if (!window.confirm(`Remove passkey "${cred.device_label}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/webauthn/credentials/${cred.id}`);
            toast.success("Passkey removed.");
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const needsReauth = e?.response?.status === 428
                || (detail && detail.code === "reauth_required");
            if (needsReauth) {
                try {
                    await reauthenticateWithPasskey();
                    await api.delete(`/webauthn/credentials/${cred.id}`);
                    toast.success("Passkey removed.");
                    await load();
                } catch (e2) {
                    toast.error("Reauth required — please try again.");
                }
            } else {
                toast.error("Failed to remove passkey.");
            }
        } finally { setBusy(false); }
    };

    const rename = async () => {
        if (!renameFor) return;
        setBusy(true);
        try {
            await api.patch(`/webauthn/credentials/${renameFor.id}`,
                { device_label: renameLabel.trim() });
            toast.success("Renamed.");
            setRenameFor(null);
            await load();
        } catch { toast.error("Rename failed."); }
        finally { setBusy(false); }
    };

    if (creds === null) return (
        <div className="p-8"><Loader2 size={16} className="animate-spin" /></div>
    );

    return (
        <div className="max-w-3xl mx-auto p-6 md:p-8" data-testid="passkeys-manager">
            <div className="mb-4 flex items-center gap-2 text-xs">
                <Link to="/security" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Security
                </Link>
            </div>

            <div className="flex items-start gap-3 mb-6">
                <div className="mt-1 rounded-full bg-blue-50 p-1.5">
                    <Fingerprint size={16} className="text-blue-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Passkeys</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        Use Touch ID, Face ID or your device security to sign in to VAULT.
                        Your fingerprint never leaves your device — VAULT only receives a
                        secure authentication confirmation.
                    </p>
                </div>
            </div>

            {/* Support banner */}
            {!supported && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 mb-4 flex items-start gap-2" data-testid="unsupported-banner">
                    <Info size={14} className="mt-0.5" />
                    <div>Passkeys aren&apos;t supported on this browser. Try Safari, Chrome or Edge on macOS, iOS, Android or Windows.</div>
                </div>
            )}
            {supported && !platformAvailable && (
                <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-sm text-stone-700 mb-4 flex items-start gap-2" data-testid="hardware-only-banner">
                    <Info size={14} className="mt-0.5" />
                    <div>No platform authenticator (Touch ID / Face ID / Windows Hello) is available here — but you can still register a hardware security key.</div>
                </div>
            )}

            {/* Add card */}
            <section className="rounded-xl border border-stone-200 bg-white p-4 mb-6" data-testid="add-passkey-card">
                <div className="text-sm font-semibold mb-2">Add a passkey</div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                        placeholder={friendlyDeviceLabel()}
                        value={addLabel}
                        onChange={(e) => setAddLabel(e.target.value)}
                        data-testid="add-passkey-label"
                        maxLength={80}
                    />
                    <Button
                        onClick={addPasskey}
                        disabled={busy || !supported}
                        data-testid="add-passkey-btn"
                    >
                        {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Plus size={12} className="mr-1" />}
                        {passkeyButtonLabel().replace("Sign in with ", "Add ")}
                    </Button>
                </div>
                <div className="text-xs text-stone-500 mt-2">
                    You can register multiple passkeys — one per Mac, iPhone, Android device or security key.
                </div>
            </section>

            {/* Existing passkeys */}
            <section data-testid="passkey-list">
                <div className="text-sm font-semibold mb-2">Your registered passkeys</div>
                {creds.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-stone-300 p-6 text-center text-sm text-stone-500">
                        No passkeys registered yet. Add one above to sign in faster next time.
                    </div>
                ) : (
                    <div className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100">
                        {creds.map((c) => (
                            <div key={c.id} className="p-3 flex items-center gap-3" data-testid={`passkey-${c.id}`}>
                                <div className="rounded-md bg-emerald-50 text-emerald-700 p-1.5"><Fingerprint size={14} /></div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium flex items-center gap-2">
                                        {c.device_label}
                                        {c.status === "active" && (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px]">
                                                <CheckCircle2 size={10} /> Active
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-stone-500 mt-0.5">
                                        Added {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                                        {" · "}
                                        Last used {c.last_used_at ? new Date(c.last_used_at).toLocaleDateString() : "never"}
                                        {c.authenticator_attachment && ` · ${c.authenticator_attachment}`}
                                    </div>
                                </div>
                                <Button variant="ghost" size="sm"
                                    onClick={() => { setRenameFor(c); setRenameLabel(c.device_label || ""); }}
                                    data-testid={`rename-btn-${c.id}`}>
                                    <Pencil size={12} />
                                </Button>
                                <Button variant="ghost" size="sm"
                                    onClick={() => removePasskey(c)}
                                    data-testid={`remove-btn-${c.id}`}>
                                    <Trash2 size={12} className="text-rose-600" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <div className="mt-6 rounded-xl border border-stone-200 bg-stone-50 p-4 text-xs text-stone-600 flex items-start gap-2">
                <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                <div>
                    Removing a passkey requires recent authentication. If you haven&apos;t
                    signed in in the last few minutes we&apos;ll prompt you for another
                    passkey verification first. Your password and 2FA remain available
                    as a fallback.
                </div>
            </div>

            {/* Rename dialog */}
            <Dialog open={!!renameFor} onOpenChange={(o) => !o && setRenameFor(null)}>
                <DialogContent data-testid="rename-dialog">
                    <DialogHeader><DialogTitle>Rename passkey</DialogTitle></DialogHeader>
                    <Input value={renameLabel} onChange={(e) => setRenameLabel(e.target.value)}
                        maxLength={80} data-testid="rename-input" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameFor(null)}>Cancel</Button>
                        <Button onClick={rename} disabled={busy || !renameLabel.trim()} data-testid="rename-save">
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
